import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useAuthStore } from '@/store/authStore';
import { AuthGuard } from '@/components/layout/AuthGuard';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { InstallPrompt } from '@/components/InstallPrompt';
import { Spinner } from '@/components/ui/Spinner';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterParentPage } from '@/pages/RegisterParentPage';
import { RegisterUniversityPage } from '@/pages/RegisterUniversityPage';
import { AddChildPage } from '@/pages/AddChildPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ParentDashboard } from '@/pages/ParentDashboard';
import { ChildLoginPage } from '@/pages/ChildLoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { StudentMorePage } from '@/pages/StudentMorePage';
import { StudentSubscriptionPage } from '@/pages/StudentSubscriptionPage';
import { ParentChildDetailView } from '@/pages/ParentChildDetailView';
import { ParentSubscriptionPage } from '@/pages/ParentSubscriptionPage';
import { ParentMorePage } from '@/pages/ParentMorePage';
import { ParentChildTimetablePage } from '@/pages/ParentChildTimetablePage';
import { StudentDashboard } from '@/pages/StudentDashboard';
import { ExamsPage as ExamsUIPage } from '@/pages/ExamsPage';
import { FlashcardSession } from '@/pages/FlashcardSession';
import { QuizSession } from '@/pages/QuizSession';
import { ExplainSession } from '@/pages/ExplainSession';
import { ConfidencePage as ConfidenceUIPage } from '@/pages/ConfidencePage';
import { ProgressPage } from '@/pages/ProgressPage';
import { TimetablePage } from '@/pages/TimetablePage';
import { ManageTimetablePage } from '@/pages/ManageTimetablePage';
import { ManageCommitmentsPage } from '@/pages/ManageCommitmentsPage';
import { ManageFreeTimePage } from '@/pages/ManageFreeTimePage';
import { ScanTimetablePage } from '@/pages/ScanTimetablePage';
import { HomeworkPage } from '@/pages/student/HomeworkPage';
import { AddHomeworkPage } from '@/pages/student/AddHomeworkPage';
import { HomeworkDetailPage } from '@/pages/student/HomeworkDetailPage';
import { HomeworkChatPage } from '@/pages/student/HomeworkChatPage';
import { ExamsPage as StudentExamsPage } from '@/pages/student/ExamsPage';

// Student pages — lazy-loaded (only routes not yet migrated to the new UI)
const RevisionPlanPage = lazy(() =>
  import('@/pages/student/RevisionPlanPage').then((m) => ({ default: m.RevisionPlanPage })),
);

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" className="text-primary-600" aria-label="Loading page…" />
    </div>
  );
}

/** Root redirect — sends an already-authenticated visitor straight to their
 * role's dashboard. Never routes through the legacy /dashboard page. */
function RootRedirect() {
  const { t } = useTranslation();
  const { isLoading, isAuthenticated, role } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" className="text-primary-600" aria-label={t('actions.loading')} />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={role === 'student' ? '/student' : '/parent'} replace />;
}

function App() {
  const isOffline = useNetworkStatus();
  return (
    <ErrorBoundary>
      {isOffline && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
          background: '#EF4444', color: 'white',
          textAlign: 'center', padding: '8px',
          fontSize: '0.85rem', fontWeight: 600,
        }}>
          ⚡ You are offline. Some features may be unavailable.
        </div>
      )}
      <InstallPrompt />
      <Routes>
        {/* ── Public routes ── */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/child-login" element={<ChildLoginPage />} />
        <Route path="/register" element={<RegisterParentPage />} />

        {/* ── Protected: parent ── */}
        <Route element={<AuthGuard requireRole="parent" />}>
          <Route path="/parent" element={<ParentDashboard />} />
          <Route path="/parent/add-child" element={<AddChildPage />} />
          <Route path="/parent/child/:childId" element={<ParentChildDetailView />} />
          <Route path="/parent/subscription" element={<ParentSubscriptionPage />} />
          <Route path="/parent/more" element={<ParentMorePage />} />
          <Route path="/parent/child/:childId/timetable" element={<ParentChildTimetablePage />} />
          <Route path="/parent/child/:childId/timetable/scan" element={<ScanTimetablePage />} />
          <Route path="/parent/child/:childId/timetable/edit" element={<ManageTimetablePage />} />
          {/* Legacy aliases */}
          <Route path="/parent-dashboard" element={<ParentDashboard />} />
        </Route>

        {/* ── Protected: student ── */}
        <Route element={<AuthGuard requireRole="student" />}>
          <Route path="/student" element={<StudentDashboard />} />
          <Route path="/student/timetable" element={<TimetablePage />} />
          <Route path="/student/timetable/scan" element={<ScanTimetablePage />} />
          <Route path="/student/timetable/edit" element={<ManageTimetablePage />} />
          <Route path="/student/timetable/commitments" element={<ManageCommitmentsPage />} />
          <Route path="/student/timetable/free-time" element={<ManageFreeTimePage />} />
          <Route path="/student/exams" element={<StudentExamsPage />} />
          <Route path="/student/flashcards" element={<FlashcardSession />} />
          <Route path="/student/quiz" element={<QuizSession />} />
          <Route path="/student/explain" element={<ExplainSession />} />
          <Route path="/student/homework" element={<HomeworkPage />} />
          <Route path="/student/homework/add" element={<AddHomeworkPage />} />
          <Route path="/student/homework/:id" element={<HomeworkDetailPage />} />
          <Route path="/student/homework/:id/exercise/:eid" element={<HomeworkChatPage />} />
          <Route path="/student/progress" element={<ProgressPage />} />
          <Route path="/student/confidence" element={<ConfidenceUIPage />} />
          <Route path="/student/more" element={<StudentMorePage />} />
          <Route path="/student/subscription" element={<StudentSubscriptionPage />} />
          <Route
            path="/student/revision-plan"
            element={
              <Suspense fallback={<PageLoader />}>
                <RevisionPlanPage />
              </Suspense>
            }
          />
          {/* Legacy aliases */}
          <Route path="/student-dashboard" element={<StudentDashboard />} />
          <Route path="/university-more" element={<StudentMorePage />} />
          <Route path="/exams" element={<ExamsUIPage />} />
          <Route path="/confidence" element={<ConfidenceUIPage />} />
        </Route>
        <Route path="/register-student" element={<RegisterUniversityPage />} />

        {/* Legacy URL compat — old child-detail links redirect to the parent home */}
        <Route path="/children/:id" element={<Navigate to="/parent" replace />} />

        {/* ── Root redirect — role-aware ── */}
        <Route path="/" element={<RootRedirect />} />

        {/* ── 404 ── */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
